import * as Dialog from '@radix-ui/react-dialog'

import { FxIcon } from '@/components/fx'
import { cn } from '@/lib/utils'

/**
 * The dialog skin these settings share, in the design's overlay shape:
 * `rounded-xl`, `surface`, `shadow-overlay`, over the scrim.
 *
 * Radix carries the focus trap, Escape, the labelled title and the return of
 * focus to whatever opened it. The same shell as `CockpitHeader`'s, kept
 * here rather than imported from it because that file is another agent's and
 * its copy is private to it; when a third screen wants this, it belongs in
 * `components/fx`.
 *
 * `wide` is for the two that hold a table. They scroll inside themselves —
 * `max-h` on the content plus an inner scroller — so the dialog's title and
 * its close button stay reachable on a laptop screen.
 */
export function DialogShell({
  open,
  onClose,
  title,
  description,
  wide = false,
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  description?: React.ReactNode
  wide?: boolean
  children: React.ReactNode
}) {
  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-[#0000008c]" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 flex max-h-[min(90vh,760px)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl bg-surface p-5 shadow-overlay xs:p-8',
            wide ? 'w-[min(960px,calc(100vw-32px))]' : 'w-[min(560px,calc(100vw-32px))]',
          )}
        >
          <div className="mb-4 flex items-start gap-4">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="m-0 text-dialog-title text-text">{title}</Dialog.Title>
              {description != null && (
                <Dialog.Description className="mt-2 text-small text-text-muted">
                  {description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close
              className="shrink-0 rounded-sm p-1 text-text hover:text-primary-text"
              aria-label="Schließen"
            >
              <FxIcon name="close" size={24} />
            </Dialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
